import type { Database } from '@/integrations/supabase/types';
import { BaseService } from '@/services/base/BaseService';

type ReachEventRow = Database['public']['Tables']['reach_events']['Row'];
type ReachEventUpdate = Database['public']['Tables']['reach_events']['Update'];
type InteractionRow = Database['public']['Tables']['reach_interactions']['Row'];

/** Verified against `reach_interactions_interaction_type_check`. */
export type ReachInteractionType =
  | 'note'
  | 'linkedin_message'
  | 'email'
  | 'phone_call'
  | 'meeting'
  | 'event_meeting'
  | 'content_shared'
  | 'reply'
  | 'follow_up'
  | 'crm_activity';

/**
 * One `reach_interactions` row scoped to this event — the Activity tab's
 * other half. `personName` falls back to a short id fragment rather than
 * rendering blank, matching `useReachOutreachQueueRows`'s convention for the
 * same situation (a name column with no fallback reads as a silent gap).
 */
export interface ReachEventInteraction {
  id: string;
  personId: string | null;
  personName: string | null;
  interactionType: ReachInteractionType;
  happenedAt: string;
  summary: string;
}

/** Verified against `reach_events_status_check` in the migration. */
export type ReachEventStatus = 'draft' | 'planning' | 'active' | 'completed' | 'archived';

/** Verified against `reach_events_participation_type_check` in the migration. */
export type ReachEventParticipationType =
  'attending' | 'exhibiting' | 'speaking' | 'sponsoring' | 'hosting';

/** A list row: the event plus the derived counts its card and page need. */
export interface ReachEventWithCounts extends ReachEvent {
  participantCount: number;
  /**
   * Participants with a latest `reach_event_assessments` row — the same
   * "does a participant have any assessment row" definition
   * `ReachEventPeopleService.listForEvent` uses to populate `score`. Not a
   * distinct-version count; one or more versions for a participant still
   * counts that participant once.
   */
  scoredCount: number;
  /** `reach_events.brief_generated_at is not null` — no separate derivation. */
  briefReady: boolean;
}

export interface ReachEvent {
  id: string;
  workspaceId: string;
  ownerId: string;
  name: string;
  slug: string;
  /**
   * `reach_events` has no dedicated `notes` column — the UX PRD §12.1
   * "Notes" field maps onto this table's only free-text column,
   * `description`.
   */
  description: string | null;
  eventUrl: string | null;
  websiteTitle?: string | null;
  websiteDescription?: string | null;
  websiteImageUrl?: string | null;
  websiteScannedAt?: string | null;
  locationName: string | null;
  city: string | null;
  region: string | null;
  country: string | null;
  startsAt: string | null;
  endsAt: string | null;
  /** Local calendar day, no timezone conversion — the source of truth for
   *  all-day calendar-bar rendering. See migration
   *  20260901223000_reach_events_date_only_fields.sql. Distinct from
   *  startsAt/endsAt, which are kept for backward compatibility with
   *  existing display sites that still carry the timezone bug these fix. */
  startsOn: string | null;
  endsOn: string | null;
  timezone: string | null;
  participationType: ReachEventParticipationType | null;
  boothLocation: string | null;
  eventOwnerUserId: string | null;
  status: ReachEventStatus;
  activeIngestBatchId: string | null;
  currentAssessmentVersion: number;
  briefGeneratedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateReachEventInput {
  workspaceId: string;
  name: string;
  description?: string | null;
  eventUrl?: string | null;
  locationName?: string | null;
  city?: string | null;
  region?: string | null;
  country?: string | null;
  startsAt?: string | null;
  endsAt?: string | null;
  /** Plain "YYYY-MM-DD" — pass the date-picker's own string through
   *  directly, never `new Date(x).toISOString()` (that's the bug this
   *  column exists to avoid). */
  startsOn?: string | null;
  endsOn?: string | null;
  participationType?: ReachEventParticipationType | null;
  boothLocation?: string | null;
  eventOwnerUserId?: string | null;
}

export interface UpdateReachEventInput {
  name?: string;
  description?: string | null;
  eventUrl?: string | null;
  locationName?: string | null;
  city?: string | null;
  region?: string | null;
  country?: string | null;
  startsAt?: string | null;
  endsAt?: string | null;
  startsOn?: string | null;
  endsOn?: string | null;
  participationType?: ReachEventParticipationType | null;
  boothLocation?: string | null;
  eventOwnerUserId?: string | null;
  status?: ReachEventStatus;
}

function mapReachEvent(row: ReachEventRow): ReachEvent {
  return {
    id: row.id,
    workspaceId: row.workspace_id,
    ownerId: row.owner_id,
    name: row.name,
    slug: row.slug,
    description: row.description,
    eventUrl: row.event_url,
    websiteTitle: row.website_title,
    websiteDescription: row.website_description,
    websiteImageUrl: row.website_image_url,
    websiteScannedAt: row.website_scanned_at,
    locationName: row.location_name,
    city: row.city,
    region: row.region,
    country: row.country,
    startsAt: row.starts_at,
    endsAt: row.ends_at,
    startsOn: row.starts_on,
    endsOn: row.ends_on,
    timezone: row.timezone,
    participationType: row.participation_type as ReachEventParticipationType | null,
    boothLocation: row.booth_location,
    eventOwnerUserId: row.event_owner_user_id,
    status: row.status as ReachEventStatus,
    activeIngestBatchId: row.active_ingest_batch_id,
    currentAssessmentVersion: row.current_assessment_version,
    briefGeneratedAt: row.brief_generated_at,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

/**
 * `reach_events.slug` matches `^[a-z0-9][a-z0-9-]{0,119}$` (migration
 * constraint) and has no separate uniqueness constraint recorded here beyond
 * the app-level de-dup this service does against the workspace's own rows.
 */
function slugify(name: string): string {
  const base = name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 100);
  return base.length > 0 ? base : 'event';
}

const EVENT_SELECT = '*';

export class ReachEventService extends BaseService {
  async list(workspaceId: string): Promise<ReachEventWithCounts[]> {
    const { data, error } = await this.client
      .from('reach_events')
      .select(EVENT_SELECT)
      .eq('workspace_id', workspaceId)
      .order('created_at', { ascending: false });
    if (error) throw error;

    const events = (data ?? []).map((row) => mapReachEvent(row as ReachEventRow));
    if (events.length === 0) return [];

    // One extra read rather than N: the grid's cards were carrying only a name,
    // a status pill and "Dates not set", which is not enough for a card to earn
    // its space. The count is the fact a person actually wants off this page.
    const { data: metricsData, error: metricsError } = await this.client.rpc(
      'get_reach_event_metrics' as never,
      {
        p_workspace_id: workspaceId,
        p_event_ids: events.map((event) => event.id),
      } as never,
    );
    if (metricsError) throw metricsError;
    const metricsByEvent = new Map(
      (
        (metricsData ?? []) as unknown as Array<{
          event_id: string;
          participant_count: number;
          scored_count: number;
        }>
      ).map((metric) => [metric.event_id, metric]),
    );

    return events.map((event) => ({
      ...event,
      participantCount: metricsByEvent.get(event.id)?.participant_count ?? 0,
      scoredCount: metricsByEvent.get(event.id)?.scored_count ?? 0,
      briefReady: event.briefGeneratedAt !== null,
    }));
  }

  async getById(id: string, workspaceId: string): Promise<ReachEvent | null> {
    const { data, error } = await this.client
      .from('reach_events')
      .select(EVENT_SELECT)
      .eq('id', id)
      .eq('workspace_id', workspaceId)
      .maybeSingle();
    if (error) throw error;
    return data ? mapReachEvent(data as ReachEventRow) : null;
  }

  /**
   * `reach_interactions` scoped to one event — the Activity tab's other half
   * alongside `reach_jobs`. There is no existing event-scoped interaction
   * reader in `ReachPersonService` (its readers are all person-scoped), so
   * this is a direct, narrowly-scoped read rather than a detour through it.
   */
  async listInteractionsForEvent(
    eventId: string,
    workspaceId: string,
  ): Promise<ReachEventInteraction[]> {
    const { data, error } = await this.client
      .from('reach_interactions')
      .select('id, person_id, interaction_type, happened_at, summary, reach_people(full_name)')
      .eq('event_id', eventId)
      .eq('workspace_id', workspaceId)
      .order('happened_at', { ascending: false });
    if (error) throw error;

    return (
      (data ?? []) as Array<
        Pick<
          InteractionRow,
          'id' | 'person_id' | 'interaction_type' | 'happened_at' | 'summary'
        > & {
          reach_people: { full_name: string } | null;
        }
      >
    ).map((row) => ({
      id: row.id,
      personId: row.person_id,
      personName: row.reach_people?.full_name ?? null,
      interactionType: row.interaction_type as ReachInteractionType,
      happenedAt: row.happened_at,
      summary: row.summary,
    }));
  }

  /** Generates a URL-safe slug for `name`, de-duplicated within the workspace. */
  private async generateUniqueSlug(name: string, workspaceId: string): Promise<string> {
    const base = slugify(name);
    const { data, error } = await this.client
      .from('reach_events')
      .select('slug')
      .eq('workspace_id', workspaceId)
      .like('slug', `${base}%`);
    if (error) throw error;
    const taken = new Set((data ?? []).map((row) => (row as { slug: string }).slug));
    if (!taken.has(base)) return base;
    let suffix = 2;
    while (taken.has(`${base}-${suffix}`)) suffix += 1;
    return `${base}-${suffix}`;
  }

  async create(input: CreateReachEventInput): Promise<ReachEvent> {
    const ownerId = await this.ensureAuthenticatedOwner();
    const slug = await this.generateUniqueSlug(input.name, input.workspaceId);

    const { data, error } = await this.client
      .from('reach_events')
      .insert({
        owner_id: ownerId,
        workspace_id: input.workspaceId,
        name: input.name,
        slug,
        description: input.description ?? null,
        event_url: input.eventUrl ?? null,
        location_name: input.locationName ?? null,
        city: input.city ?? null,
        region: input.region ?? null,
        country: input.country ?? null,
        starts_at: input.startsAt ?? null,
        ends_at: input.endsAt ?? null,
        starts_on: input.startsOn ?? null,
        ends_on: input.endsOn ?? null,
        participation_type: input.participationType ?? null,
        booth_location: input.boothLocation ?? null,
        event_owner_user_id: input.eventOwnerUserId ?? null,
        status: 'planning',
      })
      .select(EVENT_SELECT)
      .single();
    if (error) throw error;
    return mapReachEvent(data as ReachEventRow);
  }

  async update(id: string, patch: UpdateReachEventInput): Promise<ReachEvent> {
    const ownerId = await this.ensureAuthenticatedOwner();

    const updatePayload: ReachEventUpdate = {};
    if (patch.name !== undefined) updatePayload.name = patch.name;
    if (patch.description !== undefined) updatePayload.description = patch.description;
    if (patch.eventUrl !== undefined) updatePayload.event_url = patch.eventUrl;
    if (patch.locationName !== undefined) updatePayload.location_name = patch.locationName;
    if (patch.city !== undefined) updatePayload.city = patch.city;
    if (patch.region !== undefined) updatePayload.region = patch.region;
    if (patch.country !== undefined) updatePayload.country = patch.country;
    if (patch.startsAt !== undefined) updatePayload.starts_at = patch.startsAt;
    if (patch.endsAt !== undefined) updatePayload.ends_at = patch.endsAt;
    if (patch.startsOn !== undefined) updatePayload.starts_on = patch.startsOn;
    if (patch.endsOn !== undefined) updatePayload.ends_on = patch.endsOn;
    if (patch.participationType !== undefined)
      updatePayload.participation_type = patch.participationType;
    if (patch.boothLocation !== undefined) updatePayload.booth_location = patch.boothLocation;
    if (patch.eventOwnerUserId !== undefined)
      updatePayload.event_owner_user_id = patch.eventOwnerUserId;
    if (patch.status !== undefined) updatePayload.status = patch.status;

    const { data, error } = await this.client
      .from('reach_events')
      .update(updatePayload)
      .eq('id', id)
      .eq('owner_id', ownerId)
      .select(EVENT_SELECT)
      .single();
    if (error) throw error;
    return mapReachEvent(data as ReachEventRow);
  }
}

export const reachEventService = new ReachEventService();
